import { Head } from '@inertiajs/react'
import type { ReactNode } from 'react'
import Layout from '../../layouts/Layout'

interface Post {
  id: string
  title: string
  subtitle: string
  thumbnail: string
  large: string
  tint: string
  width: number
  height: number
  link: string
}

interface User {
  username: string
  fullName: string
  location: string
  postsCount: number
}

interface Props {
  user: User
  posts: Post[]
  feedUrl: string
  qrCodeUrl: string
}

export default function Show({ user, posts, feedUrl, qrCodeUrl }: Props) {
  const firstPost = posts[0]

  return (
    <Layout title={`@${user.username}`}>
      <Head>
        <meta property="og:title" content={`@${user.username}`} />
        {firstPost && (
          <meta property="og:image" content={firstPost.large} />
        )}
      </Head>

      {/* Header */}
      <div className="container mx-auto px-4 py-8 text-center">
        {qrCodeUrl && (
          <div className="mb-4">
            <img
              src={qrCodeUrl}
              alt={`Follow @${user.username}`}
              className="mx-auto w-24 h-24"
            />
          </div>
        )}
        <h1 className="text-4xl font-bold mb-2">@{user.username}</h1>
        <h2 className="text-lg text-gray-600">
          {user.fullName} • {user.location} • {user.postsCount} posts
        </h2>
      </div>

      {/* Photo Grid */}
      <div className="container mx-auto px-4 py-8 bg-gray-100">
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {posts.map((post) => (
            <li
              key={post.id}
              className="relative aspect-square rounded-lg overflow-hidden"
              title={post.title}
            >
              <img
                src={post.thumbnail}
                alt={post.subtitle}
                className="w-full h-full object-cover"
                loading="lazy"
                style={{ backgroundColor: post.tint }}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Links */}
      <div className="container mx-auto px-4 py-8 text-center">
        <ul className="flex justify-center gap-6 text-sm text-gray-600">
          <li>
            <a
              href={feedUrl}
              rel="alternate"
              type="application/rss+xml"
              className="hover:text-black transition-colors"
            >
              RSS Feed
            </a>
          </li>
        </ul>
      </div>
    </Layout>
  )
}

Show.layout = (page: ReactNode) => page
